import { GAME_CONFIG, normalizeRoomCode } from '@majority-report/shared';

/**
 * Share links, so a room can be joined by opening a URL instead of typing a
 * four-letter code.
 *
 * Canonical shape is `/r/ABCD`. `?code=ABCD` is also accepted, because that is
 * the form people end up with when they hand-edit a link or when a chat app
 * mangles the path.
 *
 * `/r/` rather than a bare `/ABCD` on purpose: the root path has to stay free
 * for real routes (`/health`, `/ws`, static assets), and a prefix means a future
 * one can never be mistaken for a room code.
 */
export const ROOM_PATH_PREFIX = '/r/';

/** The code carried by the current URL, or `null`. Never throws. */
export function readCodeFromUrl(): string | null {
  try {
    const { pathname, search } = window.location;

    if (pathname.startsWith(ROOM_PATH_PREFIX)) {
      const fromPath = normalizeRoomCode(pathname.slice(ROOM_PATH_PREFIX.length).split('/')[0]);
      if (fromPath.length === GAME_CONFIG.roomCodeLength) return fromPath;
    }

    const fromQuery = normalizeRoomCode(new URLSearchParams(search).get('code') ?? '');
    if (fromQuery.length === GAME_CONFIG.roomCodeLength) return fromQuery;

    return null;
  } catch {
    return null;
  }
}

/** Absolute link to share for a room, e.g. `https://host/r/ABCD`. */
export function buildRoomLink(code: string): string {
  return `${window.location.origin}${ROOM_PATH_PREFIX}${code}`;
}

/**
 * Drop the code from the address bar once it has been consumed.
 *
 * Without this the link stays in history: a refresh would re-read a code that
 * the stored session has already superseded, and the bar would keep advertising
 * a room the player may since have left.
 */
export function clearCodeFromUrl(): void {
  try {
    if (readCodeFromUrl() === null) return;
    window.history.replaceState(null, '', window.location.origin + '/');
  } catch {
    /* history blocked — harmless, the code is simply consumed twice. */
  }
}
